import React from 'react';
import ReactDOM from 'react-dom';
import $ from 'jquery';
import DisablePreview from './disable_preview';
import StyleguideDropdown from './styleguide-dropdown';
import StatefulAutocomplete from 'common/autocomplete/components/StatefulAutocomplete';
import { getBrowseOrSIAMUrl } from 'common/autocomplete/Util';
import { Key } from 'common/types/keyboard/key';

/**
 * Site Chrome Header Controller
 *
 * Main JavaScript controller for the platform header system. Manages responsive behavior,
 * mobile navigation, search functionality, and user interaction states across all themes.
 *
 * Key Responsibilities:
 * - Responsive layout switching between mobile and desktop modes
 * - Mobile menu open/close management with accessibility support
 * - Collapsible search bar functionality
 * - User dropdown and navigation interactions
 * - Performance optimization through cached measurements and throttling
 *
 * @module SiteChrome
 * @requires jQuery
 * @requires React
 * @requires ReactDOM
 * @since 1.0.0
 */
export default function SiteChrome() {
  // Layout state constants
  const LAYOUT_DESKTOP = 'desktop';
  const LAYOUT_MOBILE = 'mobile';
  const LAYOUT_NONE = 'none';

  const shrinkFactorEvergreen = 0.75;
  const shrinkFactorRally = 0.75;
  let $collapsibleSearchToggle;
  let $divider;
  let $evergreenCluster;
  let $navbarRight;
  let $rallySearchBox;
  let $rallyTop;
  let $rallyBottom;
  let $siteChromeContainer;
  let $siteChromeAdminHeader;
  let $siteChromeHeader;
  let $siteChromeHeaderAndAdminHeader;
  let $siteChromeHeaderDesktopNav;
  let $siteChromeHeaderMobileNav;
  let $siteChromeLogo;
  let $siteChromeLogoImg;
  let $siteChromeMobileMenu;
  let $siteChromeMobileMenuToggle;
  let $siteChromeMobileMenuCloseBtn;
  let $siteChromeNavLinks;
  let $siteName;
  let $vanishingSearchboxInput;
  let currentLayout = LAYOUT_NONE;
  let dividerWidth;
  let egClusterWidth;
  let initialBodyOverflowY;
  let logoWidth;
  let navLinkFullWidth;
  let navbarRightWidth;
  let siteChromeTemplate;
  let siteNameWidth;

  // Cached canvas for text width calculations
  const textWidthCanvas = document.createElement('canvas');
  const textWidthContext = textWidthCanvas.getContext('2d');

  // Cached jQuery selections
  let $window = null;

  // Resize throttling
  let resizeTimeout = null;

  $(function () {
    DisablePreview();
    StyleguideDropdown();

    // Cache window jQuery selection
    $window = $(window);

    $siteChromeContainer = $('#site-chrome-container');
    $siteChromeHeader = $('#site-chrome-header');
    $siteChromeAdminHeader = $('#site-chrome-admin-header');
    $siteChromeHeaderAndAdminHeader = $siteChromeHeader.add($siteChromeAdminHeader);
    $siteChromeHeaderDesktopNav = $siteChromeHeader.find('nav.desktop');
    $siteChromeHeaderMobileNav = $siteChromeHeader.find('nav.mobile');
    $siteChromeMobileMenu = $siteChromeHeader.find('.mobile-menu');
    $siteChromeMobileMenuToggle = $siteChromeHeader.find('#site-chrome-menu-toggle-btn');
    $siteChromeMobileMenuCloseBtn = $siteChromeHeader.find('.menu-close-btn');
    $siteName = $siteChromeHeader.find('a.logo .site-name');
    // Not sure if this is supposed to be leaking onto window.
    // Ignoring for lintapalooza.
    $divider = $siteChromeHeader.find('div.divider');

    $rallyTop = $siteChromeHeader.find('.rally-top');
    $rallyBottom = $siteChromeHeader.find('.rally-bottom');
    $rallySearchBox = $rallyTop.find('.searchbox');
    $collapsibleSearchToggle = $siteChromeHeaderAndAdminHeader.find('.collapsible-search-toggle');
    $vanishingSearchboxInput = $siteChromeHeaderAndAdminHeader.find('.searchbox-input.vanishing');
    siteChromeTemplate = $siteChromeHeader.attr('template');
    $evergreenCluster = $siteChromeHeader.find('.evergreen-link-cluster');

    $siteChromeLogo = $siteChromeHeader.find('a.logo');
    $siteChromeLogoImg = $siteChromeLogo.find('img.logo-image');
    $navbarRight = $siteChromeHeader.find('.navbar-right');

    initialBodyOverflowY = $('body').css('overflow-y') || 'visible';

    $siteChromeNavLinks = $siteChromeHeaderDesktopNav.find('.site-chrome-nav-links');

    gatherStaticWidths();

    checkMobileBreakpoint();
    $window.on('resize', throttledCheckMobileBreakpoint);
    $siteChromeMobileMenuToggle.on('click', toggleMobileMenu);
    $siteChromeMobileMenuCloseBtn.on('click', closeMobileMenu);
    $siteChromeMobileMenu.keydown((e) => {
      if (e.key === Key.Escape_MSIE || e.key === Key.Escape) {
        closeMobileMenu();
        e.preventDefault();
      }
    });
    mobileLanguageSwitcher($('.mobile-language-dropdown'));
    closeMobileMenu(); // Ensure initial state is closed & hidden from a11y tree.

    $collapsibleSearchToggle.on('click', toggleCollapsibleSearch).on('keypress', toggleCollapsibleByKeypress);

    $vanishingSearchboxInput.on('keyup', toggleSearchButton);
  });

  function gatherStaticWidths() {
    dividerWidth = getOuterWidth($divider);
    egClusterWidth = getOuterWidth($evergreenCluster);
    logoWidth = getOuterWidth($siteChromeLogoImg);
    navLinkFullWidth = getOuterWidth($siteChromeNavLinks);
    navbarRightWidth = getOuterWidth($navbarRight);
    siteNameWidth = getTextWidth($siteName);
  }

  /**
   * Check Mobile Breakpoint
   *
   * Determines if the header should switch between mobile and desktop layouts
   * based on available space and content width. Uses theme-specific logic
   * to calculate whether navigation elements fit in the current viewport.
   *
   * @throws {Error} If DOM elements are not properly initialized
   */
  function checkMobileBreakpoint() {
    try {
      const mobile = siteChromeTemplate === 'rally' ? isMobileRally() : isMobileEvergreen();

      // Toggle between desktop/mobile only if it is a change:
      if (!isCurrentLayoutDesktop() && !mobile) {
        showNavDesktopHeader();
      } else if (!isCurrentLayoutMobile() && mobile) {
        showNavMobileHeader();
      }

      setupGlobalHeader();
      // Undo initial hidden styling to hide searchbox during width calculations.
      // Prevents "flashing" of non-mobile search when on mobile.
      $siteChromeHeader.find('div.searchbox.hidden').removeClass('hidden');
    } finally {
      clearTimeout(resizeTimeout);
      resizeTimeout = null;
    }
  }

  /**
   * Close Mobile Menu
   *
   * Closes the mobile navigation menu and restores proper accessibility state.
   * This function moves the menu off-screen rather than hiding it, so it's critical
   * to disable all interactive elements to prevent accidental keyboard navigation.
   *
   */
  function closeMobileMenu() {
    // It's important to disable everything that has a tab-stop, since "closing"
    // the menu just means moving it off-screen.

    $siteChromeMobileMenuToggle.attr('aria-expanded', 'false');
    $siteChromeMobileMenu.removeClass('active');
    $siteChromeMobileMenu.attr('aria-hidden', 'true');
    $('body').css('overflow-y', initialBodyOverflowY);
    // Collapse the language dropdown so it doesn't reopen already-expanded next time.
    $('.mobile-language-dropdown-options').hide();
    // Disable all links so they can't accidentally be tabbed to.
    findLinksToDisableWhenMenuClosed().each((i, element) => {
      const originalHref = element.getAttribute('href');
      element.removeAttribute('href');
      element.setAttribute('data-href', originalHref);
    });
    // Don't immediately disable the close button. If we do, screen readers will sometimes
    // announce "Unavailable" after closing the menu, which makes it sound like closing the
    // menu is unavailable, when instead focus was temporarily on a disabled button.
    setTimeout(() => {
      $siteChromeMobileMenuCloseBtn.prop('disabled', true);
    });
    updateAutocomplete();
    $siteChromeMobileMenuToggle.focus();
  }

  /**
   * When the menu is closed, we just move it off-screen. To prevent the user accidentally tabbing
   * to the closed menu (via screenreader or just plain <tab>), we need to disable all links within.
   * There is no clean way to do this (<a> tags don't support a "disabled" attr) that guarantees
   * screen readers won't register the links, other than removing the "href" attr. Note aria-hidden
   * does *not* actually hide the links from tab order.
   */
  function findLinksToDisableWhenMenuClosed() {
    return $siteChromeMobileMenu.find('.menu-content a');
  }

  /**
   * Get Text Width
   *
   * Calculates the actual rendered width of text within an element using
   * canvas measurements. Includes padding and margins for accurate space
   * calculations in responsive breakpoint logic.
   *
   * @param {jQuery} $element - jQuery element containing text to measure
   * @returns {number} Total width including text, padding, and margins
   */
  function getTextWidth($element) {
    if (!$element || !$element.length) {
      return 0;
    }
    const text = $element.text();
    const fontSize = $element.css('fontSize');
    const fontFamily = $element.css('fontFamily');
    const fontWeight = $element.css('fontWeight');

    textWidthContext.font = `${fontWeight} ${fontSize} ${fontFamily}`;
    const metrics = textWidthContext.measureText(text);
    const width = Math.min(metrics.width, getMaxWidth($element));
    return width + getPaddingX($element) + getMarginX($element);
  }

  /**
   * Throttled Mobile Breakpoint Check
   *
   * Performance-optimized wrapper for breakpoint checking that prevents
   * excessive resize event handling. Uses 250ms timeout to batch resize
   * events and avoid layout thrashing.
   *
   */
  function throttledCheckMobileBreakpoint() {
    if (!resizeTimeout) {
      resizeTimeout = setTimeout(checkMobileBreakpoint, 250);
    }
  }

  function getPaddingX($element) {
    return (
      parseInt($element.css('padding-left').replace('px', '')) +
      parseInt($element.css('padding-right').replace('px', ''))
    );
  }

  function getMarginX($element) {
    return (
      parseInt($element.css('margin-left').replace('px', '')) +
      parseInt($element.css('margin-right').replace('px', ''))
    );
  }

  function getMaxWidth($element) {
    return parseInt($element.css('max-width').replace('px', ''));
  }

  // Used to debug the various widths of elements (for checking breakpoint logic, etc):
  function debugWidths(label, $element) {
    if ($element && $element.length) {
      console.log(
        `${label} → width: ${getWidth($element)}, innerWidth: ${getInnerWidth($element)}, outerWidth: ${getOuterWidth($element)}`,
      );
    }
  }

  function getWidth($element) {
    return $element && $element.length ? $element.width() : 0;
  }

  function getInnerWidth($element) {
    return $element && $element.length ? $element.width() - getPaddingX($element) : 0;
  }

  function getOuterWidth($element) {
    return $element && $element.length ? $element.width() + getPaddingX($element) + getMarginX($element) : 0;
  }

  function isCurrentLayoutDesktop() {
    return currentLayout === LAYOUT_DESKTOP;
  }

  function isCurrentLayoutMobile() {
    return currentLayout === LAYOUT_MOBILE;
  }

  function isCurrentLayoutNone() {
    return currentLayout === LAYOUT_NONE;
  }

  /**
   * Check Evergreen Mobile Breakpoint
   *
   * Determines if the Evergreen theme should switch to mobile layout based on
   * available horizontal space for navigation elements. Calculates whether
   * navigation links fit alongside logo, site name, and utility cluster.
   *
   * @returns {boolean} True if should use mobile layout, false for desktop
   */
  function isMobileEvergreen() {
    const egHeaderWidth = getWidth($siteChromeHeader.find('.header-content'));
    const siteNameMinWidth = siteNameWidth ? siteNameWidth * shrinkFactorEvergreen : 35;

    const roomForNavLinks = egHeaderWidth - logoWidth - siteNameMinWidth - egClusterWidth - dividerWidth * 2;
    return navLinkFullWidth > roomForNavLinks;
  }

  /**
   * Check Rally Mobile Breakpoint
   *
   * Determines if the Rally theme should switch to mobile layout based on
   * available space in both tiers. Checks if site name fits in top tier
   * and if navigation links fit in bottom tier.
   *
   * @returns {boolean} True if should use mobile layout, false for desktop
   */
  function isMobileRally() {
    const rallyTopWidth = getInnerWidth($rallyTop);
    const siteNameMinWidth = getTextWidth($siteName) * shrinkFactorRally;
    const searchBoxWidth = getOuterWidth($rallySearchBox);

    const roomForSiteName = rallyTopWidth - logoWidth - searchBoxWidth;

    const rallyBottomWidth = getWidth($rallyBottom);
    const roomForNavLinks = rallyBottomWidth - navbarRightWidth;
    return roomForSiteName < siteNameMinWidth || roomForNavLinks < navLinkFullWidth;
  }

  /**
   * Open Mobile Menu
   *
   * Opens the mobile navigation menu and restores interactive functionality.
   * Re-enables all links that were disabled when the menu was closed and
   * manages focus for accessibility.
   *
   */
  function openMobileMenu() {
    findLinksToDisableWhenMenuClosed().each((i, element) => {
      const originalHref = element.getAttribute('data-href');
      element.removeAttribute('data-href');
      element.setAttribute('href', originalHref);
    });
    $siteChromeMobileMenuCloseBtn.prop('disabled', false);
    $siteChromeMobileMenu.addClass('active');
    $siteChromeMobileMenu.attr('aria-hidden', 'false');
    $siteChromeMobileMenuToggle.attr('aria-expanded', 'true');
    // Disable body from scrolling while menu is open
    $('body').css('overflow-y', 'hidden');
    updateAutocomplete();
    // jQuery's focus() doesn't work consistently here for some reason.
    const firstLink = $siteChromeMobileMenu.find('.menu-content a')[0];
    if (firstLink) {
      firstLink.focus();
    }
  }

  function mobileLanguageSwitcher($div) {
    $div.children('.mobile-language-dropdown-title').on('click', function () {
      const $options = $div.children('.mobile-language-dropdown-options');
      const $mobileMenu = $('.mobile-menu');
      $options.slideToggle('fast');
      // Offset relative to `.mobile-menu` itself, since it scrolls its own content and
      // `.offset().top` (document-relative) isn't a valid scrollTop target for it.
      const optionsTop = $options[0].getBoundingClientRect().top;
      const menuTop = $mobileMenu[0].getBoundingClientRect().top;
      $mobileMenu.animate(
        {
          scrollTop: $mobileMenu.scrollTop() + optionsTop - menuTop,
        },
        'fast',
      );
    });
  }

  function setCurrentLayout(layout) {
    currentLayout = layout;
  }

  /* This section handles some CSS adjustments for when we're rendering an additional header
   * this header would be either the #custom-global-banner
   * which can be set up in /admin/site_appearance when enable_custom_global_banner is true
   * OR the .usa-header (_usds_header.html.erb), present when enable_usds_global_header is true
   * These banners are the same regardless of anon, community or roled user status, unlike site_chrome
   */
  function setupGlobalHeader() {
    const $siteChromeUSDSHeader = $siteChromeContainer.find('.usa-banner');
    const mobileBreakpoint = 800; // make sure this matches usds_header.scss
    const isMobile = $window.width() < mobileBreakpoint;
    const isStoryView = $('#story-view').length > 0;
    const $customBanner = $('#custom-global-banner');
    const isCustomBanner = $customBanner.length > 0;

    const $body = $('#site-chrome-container.non-branded');
    if ($siteChromeUSDSHeader.length || isCustomBanner) {
      // guh this is SO gross but the admin header is fixed
      // the longer i work on this the more awful it becomes

      // Returns height of USDS header or Global Banner, whichever is present
      const getGlobalBannerHeight = (expanded) => {
        if (isCustomBanner) {
          const customBannerHeight = $customBanner.outerHeight();
          return customBannerHeight;
        }
        // Beware! Magic numbers!
        // usds numbers should be checked against _usds_header.scss
        const usdsContractedSize = isMobile ? 56 : 28;
        const usdsExpandedSize = isMobile ? 302 : 146;
        const usdsHeaderSize = expanded ? usdsExpandedSize : usdsContractedSize;
        return usdsHeaderSize;
      };

      const pushContent = (expanded) => {
        if (!$siteChromeAdminHeader) {
          return;
        }

        const globalBannerHeight = getGlobalBannerHeight(expanded);
        const contentPanelTop = 60; // True magic, not sure how this is calculated.
        const headerHeight = 56; // check against _tyler_forge_omnibar.scss

        const storyEditBarHeight = $('#storyteller-edit-header').outerHeight() || 0;
        const $downtimeHeader = $('#story-save-error-bar, #downtime-notice-bar');
        const downtimeNoticeHeight =
          $downtimeHeader && $downtimeHeader.is(':visible') ? $downtimeHeader.outerHeight() : 0;

        const totalHeadersHeight = headerHeight + globalBannerHeight;
        const totalHeadersHeightPx = `${totalHeadersHeight}px`;

        // i'm so sorry
        if ($body.length > 0 && !isStoryView) {
          $body.css('margin-bottom', totalHeadersHeightPx);
        }
        $siteChromeUSDSHeader.height(globalBannerHeight);

        // this is extra awful. Storyteller is special and is the only thing that
        // uses site chrome but also renders its own *fixed* elements. And not just one, but many.
        // That means that just setting the margin on the site-chrome container is insufficient to push
        // it to the right part of the page when opening/closing the USDS header.
        [
          ['#storyteller-edit-header', totalHeadersHeight],
          ['#story-save-error-bar, #downtime-notice-bar', storyEditBarHeight],
          ['#story-edit #rich-text-editor-toolbar-wrapper', totalHeadersHeight + downtimeNoticeHeight],
          ['#content-side-bar-wrapper', globalBannerHeight],
          ['#settings-panel-left', globalBannerHeight],
          // this is the only non-storyteller element-- the leftside global nav drawer
          ['#forge-nav-drawer>forge-list:first-child', globalBannerHeight + headerHeight],
        ].forEach(([selector, top]) => {
          const $el = $(selector);
          if ($el.length > 0) {
            $el.css('margin-top', top);
          }
        });

        // Additional pushing for fixed top elements.
        $('#story-save-error-bar, #downtime-notice-bar').css('top', 'unset');
        $('#story-edit .content-panel').css('top', totalHeadersHeight + contentPanelTop);
      };

      var $expand = $siteChromeUSDSHeader.find('#expand-usds-header');
      const isCurrentlyOpen = () => $expand.attr('aria-expanded') === 'true';
      pushContent(isCurrentlyOpen());

      if ($expand) {
        $expand.off('click').click(function () {
          var isOpen = isCurrentlyOpen();
          pushContent(!isOpen);

          if (isOpen) {
            $siteChromeUSDSHeader.removeClass('expanded');
            $expand.attr('aria-expanded', 'false');
          } else {
            $siteChromeUSDSHeader.addClass('expanded');
            $expand.attr('aria-expanded', 'true');
          }
        });
      }
    }
  }

  function showNavDesktopHeader() {
    // Set current layout to desktop:
    setCurrentLayout(LAYOUT_DESKTOP);

    // Hide mobile nav
    $siteChromeHeaderMobileNav.css('display', 'none');
    $siteChromeHeader.find('.mobile-menu').attr('role', null).attr('hidden', 'true');
    // Close mobile menu if it is open
    if ($siteChromeHeader.find('.mobile-menu').hasClass('active')) {
      closeMobileMenu();
    }

    // Show rally search box in mobile mode:
    const $searchbox = $siteChromeHeader.find('.rally-top .searchbox');
    if (siteChromeTemplate === 'rally') {
      $searchbox.css('display', 'inline-block');
    }

    // Show desktop nav
    $siteChromeHeaderDesktopNav
      .css('display', 'flex')
      .attr('aria-hidden', 'false')
      .attr('role', 'navigation');
  }

  function showNavMobileHeader() {
    setCurrentLayout(LAYOUT_MOBILE);

    // Hide desktop nav
    $siteChromeHeaderDesktopNav.attr('role', null).css('display', 'none').attr('hidden', 'true');

    // Show mobile nav
    $siteChromeHeaderMobileNav.css('display', 'flex');

    // Hide rally search box in mobile mode:
    if (siteChromeTemplate === 'rally') {
      const $searchbox = $siteChromeHeader.find('.rally-top .searchbox');
      $searchbox.css('display', 'none');
    }

    const $mobileMenu = $siteChromeHeader.find('.mobile-menu');
    $mobileMenu.attr('aria-hidden', 'false').prop('hidden', false).attr('role', 'navigation');
  }

  function toggleCollapsibleByKeypress(event) {
    // 13 === ENTER, 32 === SPACE
    if ([13, 32].includes(event.which)) {
      event.target.dispatchEvent(new Event('click', { bubbles: true }));
    }
  }

  /**
   * Toggle Collapsible Search
   *
   * Expands or collapses the search bar and manages focus and event handling.
   * Sets up document-level event listeners for closing search on outside clicks
   * or escape key press.
   *
   */
  function toggleCollapsibleSearch() {
    const $searchbox = $(this).siblings('.searchbox');
    $searchbox.toggleClass('expanded');
    $searchbox.find('input').trigger('focus');

    if ($searchbox.hasClass('expanded')) {
      $searchbox.attr('aria-expanded', 'true');
    }

    // Close searchbox on click outside of box
    $(document).on('mouseup', function (e) {
      if (!$searchbox.is(e.target) && $searchbox.has(e.target).length === 0) {
        $searchbox.removeClass('expanded');
        $searchbox.attr('aria-expanded', 'false');
      }
    });

    // Close searchbox on ESCAPE key
    $(document).on('keyup', function (e) {
      if (e.keyCode === 27) {
        $searchbox.removeClass('expanded');
        $searchbox.attr('aria-expanded', 'false');
      }
    });
  }

  /**
   * Toggle Mobile Menu
   *
   * Switches mobile menu between open and closed states based on current state.
   *
   */
  function toggleMobileMenu() {
    if ($siteChromeMobileMenu.hasClass('active')) {
      closeMobileMenu();
    } else {
      openMobileMenu();
    }
  }

  // Button appears only if text has been entered.
  function toggleSearchButton() {
    const $searchButton = $(this).closest('form').find('.search-button');
    if (this.value !== '') {
      $searchButton.fadeIn(50);
    } else {
      $searchButton.fadeOut(50);
    }
  }

  function updateAutocomplete() {
    const container = $siteChromeMobileMenu.find('div.searchbox')[0];

    if (!container) {
      return;
    }

    const options = {
      animate: false,
      mobile: true,
      disabled: !$siteChromeMobileMenu.hasClass('active'),
      onChooseResult: (searchTerm) => {
        window.location.href = getBrowseOrSIAMUrl(searchTerm, true /* anonymous */);
      },
    };

    ReactDOM.render(<StatefulAutocomplete options={options} />, container);
  }
}
